import { useMemo } from "react";
import { BrandSetupData } from "../../../../../../utils/firebase/brandSetup";
import { PaymentSetupData } from "../../../../../../utils/firebase/paymentSetup";

export interface BrandSetupProgress {
  completedSteps: number;
  totalSteps: number;
  isLoaded: boolean;
}

export const computeBrandSetupProgress = (
  brandSetupData: BrandSetupData | null,
  dataLoaded: boolean,
  paymentSetupData: PaymentSetupData | null,
  paymentSetupLoaded: boolean
): BrandSetupProgress => {
  if (!dataLoaded || !paymentSetupLoaded) {
    return {
      completedSteps: 0,
      totalSteps: 3,
      isLoaded: false,
    };
  }

  const isManualSetup = brandSetupData?.setupMethod === "manual";
  const totalSteps = isManualSetup ? 2 : 3;

  let completedSteps = 0;

  if (!brandSetupData) {
    completedSteps = 0;
  } else if (
    brandSetupData.status === "completed" ||
    brandSetupData.progressTracker === "published"
  ) {
    completedSteps = totalSteps;
  } else if (brandSetupData.progressTracker) {
    switch (brandSetupData.progressTracker) {
      case "brand_added":
        completedSteps = 1;
        break;
      case "assets_selected":
        completedSteps = isManualSetup ? 1 : 2;
        break;
      case "publish_started":
        completedSteps = totalSteps - 1;
        break;
      case "customization_started":
      case "customization_completed":
        completedSteps = totalSteps - 1;
        break;
      default:
        completedSteps = 0;
    }
  }

  return {
    completedSteps,
    totalSteps,
    isLoaded: true,
  };
};

export const useBrandSetupProgress = (
  charityId: string,
  brandSetupData: BrandSetupData | null,
  dataLoaded: boolean,
  paymentSetupData: PaymentSetupData | null,
  paymentSetupLoaded: boolean
): BrandSetupProgress => {
  return useMemo(
    () =>
      computeBrandSetupProgress(
        brandSetupData,
        dataLoaded,
        paymentSetupData,
        paymentSetupLoaded
      ),
    [brandSetupData, dataLoaded, paymentSetupData, paymentSetupLoaded]
  );
};
